import { useQuery } from "@apollo/client";
import { FC, Fragment, useState } from "react";
import styled from "styled-components";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import {
  GET_CARDS_ADMIN,
  GET_CARDS_ADMIN_SEASON,
} from "../../query/CardQueries";
import { StyledChartContainer, StyledSection } from "../Chart/common/styles";
import Error from "../Error/QueryError";
import Loading from "../Loading";
import { H1 } from "../Title";
import { getBackgroundFromSeason } from "./BoosterItem";

const MAX_SEASON = 30;

type SeasonRow = {
  season: number;
  catalogSize: number;
  cardsPerBooster: number;
  boostersClosed: number;
  boostersOpened: number;
  boostersDistributed: number;
  boosterHolders: number;
  cardsInCirculation: number;
  cardsFromBooster: number;
  cardsFoil: number;
  cardsUnique: number;
  owners: number;
};

const Wrapper = styled.div`
  padding: 1rem;
`;

/* Le body n'a pas de fond défini : sans panneau sombre, le texte blanc du
   tableau serait illisible. Même parti pris que les autres écrans admin. */
const Panel = styled.div`
  background: rgba(0, 0, 0, 0.85);
  border: 1px solid #444;
  border-radius: 8px;
  padding: 1rem;
  color: white;
`;

const Note = styled.p`
  background: rgba(0, 0, 0, 0.6);
  border-left: 3px solid #ff9800;
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
`;

const Scroller = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th,
  td {
    padding: 0.4rem 0.6rem;
    border-bottom: 1px solid #444;
    text-align: right;
    white-space: nowrap;
  }

  th {
    color: #ccc;
    font-weight: normal;
    text-align: right;
    position: sticky;
    top: 0;
    background: #1b1b1b;
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }

  tbody tr:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const NormalRow = styled.tr``;

/** Saison sans catalogue ni circulation : affichée en creux, à venir. */
const EmptyRow = styled.tr`
  opacity: 0.35;
`;

const SeasonCell = styled.td`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const BoosterBack = styled.div<{ season: string }>`
  width: 34px;
  height: 48px;
  border-radius: 3px;
  border: 1px solid #ccc;
  outline: 2px groove rgba(17, 120, 1, 1);
  flex-shrink: 0;
  background-color: rgba(17, 120, 1, 1);
  ${(props) => getBackgroundFromSeason(props.season)}
`;

const DetailButton = styled.button`
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.2rem 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;

  &:disabled {
    background: #555;
    cursor: default;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const DetailBox = styled.div`
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid #444;
  border-radius: 6px;
  padding: 0.75rem;

  h3 {
    font-size: 0.9rem;
    color: #ccc;
    margin: 0 0 0.5rem;
  }

  ol {
    margin: 0;
    padding-left: 1.2rem;
    font-size: 0.8rem;
    line-height: 1.6;
  }
`;

const num = (n: number) => n.toLocaleString("fr-FR");

const SeasonDetail: FC<{ season: number }> = ({ season }) => {
  const { loading, error, data } = useQuery(GET_CARDS_ADMIN_SEASON, {
    variables: { season },
  });

  if (loading) return <Loading />;
  if (error) return <Error error={error} />;

  const detail = data?.cardsAdminSeason;
  if (!detail) return null;

  const { mostOwned = [], leastOwned = [], topOwners = [] } = detail;

  return (
    <DetailGrid>
      <DetailBox>
        <h3>Cartes les plus détenues</h3>
        <ol>
          {mostOwned.map((c: any) => (
            <li key={`most-${c.number}`}>
              #{c.number} {c.name} ({c.rarity}) — {num(c.copies)}
            </li>
          ))}
        </ol>
      </DetailBox>
      <DetailBox>
        <h3>Cartes les plus rares en circulation</h3>
        <ol>
          {leastOwned.map((c: any) => (
            <li key={`least-${c.number}`}>
              #{c.number} {c.name} ({c.rarity}) — {num(c.copies)}
            </li>
          ))}
        </ol>
      </DetailBox>
      <DetailBox>
        <h3>Top 10 collectionneurs</h3>
        <ol>
          {topOwners.map((o: any) => (
            <li key={o.userId}>
              {o.username} — {num(o.copies)} ex. / {num(o.distinctCards)} uniques
            </li>
          ))}
        </ol>
      </DetailBox>
    </DetailGrid>
  );
};

export const CardsAdmin: FC = () => {
  const isAdmin = useIsAdmin();
  const [openSeason, setOpenSeason] = useState<number | null>(null);
  const { loading, error, data } = useQuery(GET_CARDS_ADMIN, {
    variables: { maxSeason: MAX_SEASON },
    skip: !isAdmin,
  });

  if (!isAdmin) {
    return (
      <StyledChartContainer>
        <StyledSection>
          <h1>Accès non autorisé</h1>
          <p>Vous n'avez pas la permission de voir cette page.</p>
        </StyledSection>
      </StyledChartContainer>
    );
  }

  if (loading) return <Loading />;
  if (error) return <Error error={error} />;

  const rows: SeasonRow[] = data?.cardsAdmin || [];

  return (
    <Wrapper>
      <H1>Cartes (Admin)</H1>

      <Panel>
      <Note>
        <strong>Boosters ouverts : estimation, pas une mesure.</strong> Ouvrir un
        booster décrémente sa quantité sans laisser de trace. Le chiffre est
        déduit des cartes issues de boosters divisées par la composition d'un
        booster de la saison. Les cartes uniques (foil attribué au joueur
        représenté) sont exclues du calcul car elles ne viennent pas d'un
        booster. « Distribués » vaut donc ouverts estimés + fermés réels.
      </Note>

      <Scroller>
        <Table>
          <thead>
            <tr>
              <th>Saison</th>
              <th>Catalogue</th>
              <th>Cartes / booster</th>
              <th>Distribués</th>
              <th>Ouverts (est.)</th>
              <th>Fermés</th>
              <th>Détenteurs</th>
              <th>Cartes en circulation</th>
              <th>dont foil</th>
              <th>dont uniques</th>
              <th>Collectionneurs</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isEmpty =
                row.catalogSize === 0 &&
                row.cardsInCirculation === 0 &&
                row.boostersClosed === 0;
              const Row = isEmpty ? EmptyRow : NormalRow;
              return (
                <Fragment key={`s-${row.season}`}>
                  <Row>
                    <SeasonCell>
                      <BoosterBack season={String(row.season)} />
                      <span>S{row.season}</span>
                    </SeasonCell>
                    <td>{num(row.catalogSize)}</td>
                    <td>{row.cardsPerBooster || "—"}</td>
                    <td>{num(row.boostersDistributed)}</td>
                    <td>{num(row.boostersOpened)}</td>
                    <td>{num(row.boostersClosed)}</td>
                    <td>{num(row.boosterHolders)}</td>
                    <td>{num(row.cardsInCirculation)}</td>
                    <td>{num(row.cardsFoil)}</td>
                    <td>{num(row.cardsUnique)}</td>
                    <td>{num(row.owners)}</td>
                    <td>
                      <DetailButton
                        disabled={isEmpty}
                        onClick={() =>
                          setOpenSeason(
                            openSeason === row.season ? null : row.season
                          )
                        }
                      >
                        {openSeason === row.season ? "Fermer" : "Détail"}
                      </DetailButton>
                    </td>
                  </Row>
                  {openSeason === row.season && (
                    <tr>
                      <td colSpan={12}>
                        <SeasonDetail season={row.season} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </Scroller>
      </Panel>
    </Wrapper>
  );
};

export default CardsAdmin;
